import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { usePxToVw } from '../../hooks/usePxToVw';

const WayfinderDialog = ({
  resetSession,
  open,
  onClose,
  settings,
  brandColor,
  brandFont,
  countdown = null,
  showCountdown = true,
  onClick, // Primary button click handler
  secondaryButton, // Secondary button configuration
  tertiaryButton, // Tertiary button configuration
  avatarScreenMode = false, // New prop to indicate avatar screen mode
}) => {
  const { pxToVw } = usePxToVw();
  const [localCountdown, setLocalCountdown] = useState(countdown || 5);

  console.log('timeout countdown', settings, countdown, showCountdown);

  // Helper function to safely render text (handles both strings and objects)
  const renderText = (textValue) => {
    if (!textValue) return '';
    
    // If it's already a string, return it
    if (typeof textValue === 'string') return textValue;
    
    // If it's an object, it means the parent component didn't extract the language
    // This shouldn't happen, but we'll handle it gracefully by returning empty string
    // and logging a warning
    if (typeof textValue === 'object') {
      console.warn('WayfinderDialog received an object instead of a string for text:', textValue);
      return '';
    }
    
    return String(textValue);
  };

  useEffect(() => {
    if (showCountdown && open && localCountdown > 0) {
      const timer = setInterval(() => {
        setLocalCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            if (onClose) onClose();
            resetSession();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showCountdown, open, localCountdown, onClose]);

  // Reset countdown when dialog opens
  useEffect(() => {
    if (open) {
      setLocalCountdown(countdown || 5);
    }
  }, [open, countdown]);

  // Get box shadow string - match the frontend's getBoxShadow function
  const getBoxShadow = () => {
    const shadow = settings?.button?.shadow;
    if (!shadow) return 'none';

    const shadowDistance = shadow.shadowDistance || 2;
    const shadowSize = shadow.shadowSize || 5;
    const shadowColor = shadow.shadowColor || '#000000';
    const shadowAlpha = shadow.shadowAlpha || 1;

    // Convert hex to rgba with alpha
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    return `${shadowDistance}px ${shadowDistance}px ${shadowSize}px ${hexToRgba(shadowColor, shadowAlpha)}`;
  };

  // Get button box shadow
  const getButtonBoxShadow = () => {
    const shadow = settings?.button?.shadow;
    if (!shadow) return 'none';

    return getBoxShadow();
  };

  if (!settings || !open) return null;

  // Default settings from your frontend
  const defaultSettings = {
    backgroundColor: '#FFFFFF',
    borderColor: brandColor || '#007AFF',
    borderWidth: 2,
    popupPadding: 24,
    borderRadius: 12, // Changed from 20 to 12 to match frontend
    popupWidth: 320,
    fontFamily: brandFont || 'Arial, sans-serif',
    text: {
      content: 'Session Timeout',
      color: '#000000',
      fontSize: 16,
      fontWeight: 400,
      textAlign: 'center',
    },
    button: {
      text: 'Continue',
      textColor: '#FFFFFF',
      backgroundColor: brandColor || '#007AFF',
      borderRadius: 8,
      fontSize: 16,
      padding: 10,
      width: 200,
      height: 40,
    },
  };

  // Merge with provided settings
  const mergedSettings = {
    ...defaultSettings,
    ...settings,
    text: {
      ...defaultSettings.text,
      ...(settings?.text || {}),
    },
    subtitle: {
      ...(settings?.subtitle || {}),
    },
    countdown: {
      ...(settings?.countdown || {}),
    },
    button: {
      ...defaultSettings.button,
      ...(settings?.button || {}),
    },
  };

  return (
    <Box
      // onClick={onClose}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        padding: '20px',
        cursor: 'pointer',
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: mergedSettings.backgroundColor,
          padding: pxToVw(`${mergedSettings.popupPadding}`),
          borderRadius: `${mergedSettings.borderRadius}px`,
          textAlign: 'center',
          width: pxToVw(`${mergedSettings.popupWidth}`),
          maxWidth: '90vw',
          border: `${mergedSettings.borderWidth}px solid ${mergedSettings.borderColor}`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          fontFamily: mergedSettings.fontFamily,
          cursor: 'default',
        }}
      >
        {/* Main Text */}
        {mergedSettings.text?.content && (
          <Typography
            sx={{
              color: mergedSettings.text.color,
              fontSize: pxToVw(`${mergedSettings.text.fontSize}`),
              fontWeight: mergedSettings.text.fontWeight,
              fontFamily: mergedSettings.fontFamily,
              marginBottom: mergedSettings.subtitle?.content ? '12px' : '24px',
              lineHeight: 1.4,
              textAlign: mergedSettings.text.textAlign,
            }}
          >
            {renderText(mergedSettings.text.content)}
          </Typography>
        )}

        {/* Subtitle Text */}
        {mergedSettings.subtitle?.content && (
          <Typography
            sx={{
              color: mergedSettings.subtitle.color || '#666666',
              fontSize: pxToVw(`${mergedSettings.subtitle.fontSize || 16}`),
              fontWeight: mergedSettings.subtitle.fontWeight || 400,
              fontFamily: mergedSettings.fontFamily,
              marginBottom: '24px',
              lineHeight: 1.4,
              textAlign: mergedSettings.subtitle.textAlign || 'center',
            }}
          >
            {renderText(mergedSettings.subtitle.content)}
          </Typography>
        )}

        {/* Countdown Display - positioned below the subtitle */}
        {showCountdown && (
          <Typography
            sx={{
              textAlign: 'center',
              color: mergedSettings.countdown?.color || mergedSettings.text.color,
              fontSize: pxToVw(`${mergedSettings.countdown?.fontSize || mergedSettings.text.fontSize}`),
              fontWeight: mergedSettings.countdown?.fontWeight || mergedSettings.text.fontWeight,
              fontFamily: mergedSettings.fontFamily,
              marginBottom: '24px',
              lineHeight: 1.4,
            }}
          >
            {localCountdown}
          </Typography>
        )}

        {/* Button(s) */}
        {mergedSettings.button && (
          <Box
            sx={{
              display: 'flex',
              // flexDirection: avatarScreenMode ? 'column' : (secondaryButton ? 'row' : 'column'),
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              
              alignItems: 'center',
              padding: pxToVw(`${mergedSettings.button?.containerPadding || 16}`),
              
            }}
          >
            {/* Primary Button */}
            <button
              onClick={onClick || onClose}
              style={{
                color: mergedSettings.button.textColor,
                backgroundColor: mergedSettings.button.backgroundColor,
                borderRadius: `${mergedSettings.button.borderRadius}px`,
                padding: pxToVw(`${mergedSettings.button.padding}`),
                border: 'none',
                cursor: 'pointer',
                fontSize: pxToVw(`${mergedSettings.button.fontSize}`),
                fontWeight: 600,
                boxShadow: getButtonBoxShadow(),
                fontFamily: mergedSettings.fontFamily,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 'auto', // Changed from fixed width to auto
                minHeight: 'auto', // Changed from fixed height to auto
                whiteSpace: 'nowrap', // Prevent text wrapping
                letterSpacing: `${mergedSettings.button?.letterSpacing || 0}px`,
                textTransform: mergedSettings.button?.textTransform || 'none',
                transition: 'all 0.2s ease',
                opacity: mergedSettings.button?.opacity || 1,
              }}
              onMouseOver={e => {
                if (mergedSettings.button?.hoverBackgroundColor) {
                  e.target.style.backgroundColor = mergedSettings.button.hoverBackgroundColor;
                }
                if (mergedSettings.button?.hoverTextColor) {
                  e.target.style.color = mergedSettings.button.hoverTextColor;
                }
              }}
              onMouseOut={e => {
                e.target.style.backgroundColor = mergedSettings.button.backgroundColor;
                e.target.style.color = mergedSettings.button.textColor;
              }}
            >
              {renderText(mergedSettings.button.text)}
            </button>

            {/* Secondary Button */}
            {secondaryButton && (
              <button
                onClick={secondaryButton.onClick}
                style={{
                  color: secondaryButton.textColor || '#FFFFFF',
                  backgroundColor: secondaryButton.backgroundColor || '#666666',
                  borderRadius: `${secondaryButton.borderRadius || mergedSettings.button.borderRadius}px`,
                  padding: pxToVw(`${secondaryButton.padding || mergedSettings.button.padding}`),
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: pxToVw(`${secondaryButton.fontSize || mergedSettings.button.fontSize}`),
                  fontWeight: 600,
                  boxShadow: secondaryButton.shadow ? getBoxShadow() : getButtonBoxShadow(),
                  fontFamily: mergedSettings.fontFamily,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 'auto', // Changed from fixed width to auto
                  minHeight: 'auto', // Changed from fixed height to auto
                  whiteSpace: 'nowrap', // Prevent text wrapping
                  letterSpacing: `${mergedSettings.button?.letterSpacing || 0}px`,
                  textTransform: mergedSettings.button?.textTransform || 'none',
                  transition: 'all 0.2s ease',
                  opacity: mergedSettings.button?.opacity || 1,
                }}
                onMouseOver={e => {
                  e.target.style.opacity = '0.8';
                }}
                onMouseOut={e => {
                  e.target.style.opacity = mergedSettings.button?.opacity || 1;
                }}
              >
                {renderText(secondaryButton.text || 'Cancel')}
              </button>
            )}

            {/* Tertiary Button */}
            {tertiaryButton && (
              <button
                onClick={tertiaryButton.onClick}
                style={{
                  color: tertiaryButton.textColor || '#FFFFFF',
                  backgroundColor: tertiaryButton.backgroundColor || '#666666',
                  borderRadius: `${tertiaryButton.borderRadius || mergedSettings.button.borderRadius}px`,
                  padding: pxToVw(`${tertiaryButton.padding || mergedSettings.button.padding}`),
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: pxToVw(`${tertiaryButton.fontSize || mergedSettings.button.fontSize}`),
                  fontWeight: 600,
                  boxShadow: tertiaryButton.shadow ? getBoxShadow() : getButtonBoxShadow(),
                  fontFamily: mergedSettings.fontFamily,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 'auto', // Changed from fixed width to auto
                  minHeight: 'auto', // Changed from fixed height to auto
                  whiteSpace: 'nowrap', // Prevent text wrapping
                  letterSpacing: `${mergedSettings.button?.letterSpacing || 0}px`,
                  textTransform: mergedSettings.button?.textTransform || 'none',
                  transition: 'all 0.2s ease',
                  opacity: mergedSettings.button?.opacity || 1,
                }}
                onMouseOver={e => {
                  e.target.style.opacity = '0.8';
                }}
                onMouseOut={e => {
                  e.target.style.opacity = mergedSettings.button?.opacity || 1;
                }}
              >
                {renderText(tertiaryButton.text || 'Close')}
              </button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WayfinderDialog;
